import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth-service.service';
import { ChatService } from './chat.service';
import { SessionStateService } from './session-state.service';
import { SweetAlertService } from './sweet-alert.service';

/**
 * Ends the session for good when the API says the account is no longer allowed in.
 *
 * The sign-out deliberately does not hang off the dialog's promise. A deactivated account fails
 * every in-flight request at once, and several components answer an HTTP error by raising their own
 * SweetAlert; SweetAlert2 shows one popup at a time, so ours gets replaced and its promise never
 * settles. SessionStateService plus SweetAlertService.suppressed() stops that for alerts that go
 * through the wrapper, but sixteen call sites use Swal directly, so the timer below is what
 * actually guarantees the user ends up signed out.
 */
@Injectable({ providedIn: 'root' })
export class SessionTerminationService {
  /** Long enough to read one sentence, short enough that a clobbered dialog cannot strand anyone. */
  private static readonly SIGN_OUT_AFTER_MS = 10000;

  /**
   * Deliberately NOT environment.RedirectSiteUrlWhenLock. That URL belongs to the panic button in
   * WarningIconComponent, where a user locks their own account and the point is to leave no trace
   * of the portal on screen - it sends them to a neutral site. Someone deactivated by an
   * administrator did not ask to hide: they need to see where they are and how to get in touch.
   */
  private static readonly SIGN_OUT_TO = '/login';

  /** Standalone, guard-free page. Reached instead of the shell when sign-in itself is refused. */
  private static readonly DEACTIVATED_PAGE = '/account-deactivated';

  constructor(
    private authService: AuthService,
    private chatService: ChatService,
    private router: Router,
    private sessionState: SessionStateService,
    private sweetAlertService: SweetAlertService
  ) {}

  get isTerminating(): boolean {
    return this.sessionState.isTerminating;
  }

  deactivated(): void {
    // Refused at sign-in rather than mid-session: the user has not started anything, so send them
    // to a page that explains it instead of dropping them into the shell and yanking them back out.
    if (this.sessionState.isSigningIn) {
      this.refusedAtSignIn();
      return;
    }

    this.terminate(
      'Account deactivated',
      'Your account has been deactivated. Please contact support if you believe this is a mistake.'
    );
  }

  /**
   * No dialog and no redirect through B2C - the page itself carries the message, and it can only do
   * that if the browser stays put. AccountDeactivatedComponent is what ends the session, by
   * clearing the MSAL cache on arrival; doing it there rather than here means a refresh of the page
   * clears it again.
   */
  private refusedAtSignIn(): void {
    if (!this.sessionState.claimTermination()) {
      return;
    }

    this.chatService.stopConnection();
    this.router.navigate([SessionTerminationService.DEACTIVATED_PAGE]);
  }

  private terminate(title: string, message: string): void {
    if (!this.sessionState.claimTermination()) {
      return;
    }

    // The hub captured its access token when it connected, so it outlives the sign-out unless it is
    // stopped here.
    this.chatService.stopConnection();

    let signedOut = false;
    const signOutOnce = () => {
      if (signedOut) {
        return;
      }
      signedOut = true;
      this.signOut();
    };

    // Whichever happens first: the user acknowledges, the dialog's own timer runs out, or the
    // fallback fires because the dialog was never shown or was replaced by another popup.
    setTimeout(signOutOnce, SessionTerminationService.SIGN_OUT_AFTER_MS);

    this.sweetAlertService
      .fireCritical({
        title,
        text: message,
        icon: 'error',
        allowOutsideClick: false,
        confirmButtonText: 'OK',
        timer: SessionTerminationService.SIGN_OUT_AFTER_MS,
        timerProgressBar: true
      })
      .then(signOutOnce, signOutOnce);
  }

  private signOut(): void {
    try {
      this.authService.logout(SessionTerminationService.SIGN_OUT_TO);
    } catch (error) {
      // Leaving a stale MSAL cache behind would let the user back into the shell with a token the
      // API already rejects, so clear it by hand and reload.
      console.error('Sign-out after deactivation failed; clearing local state instead', error);
      localStorage.clear();
      window.location.href = '/login';
    }
  }
}
